import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, CreditCard, BookOpen, ClipboardList,
  CalendarCheck, FileText, Award, FileDown, LogOut, Menu, X,
  SlidersHorizontal, TicketPercent
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import BrandLogo from "../../components/BrandLogo";

const navItems = [
  { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/admin/students", label: "Students", icon: Users },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/coupons", label: "Coupons", icon: TicketPercent },
  { to: "/admin/courses", label: "Courses", icon: BookOpen },
  { to: "/admin/enrollments", label: "Enrollments", icon: ClipboardList },
  { to: "/admin/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/admin/assignments", label: "Assignments", icon: FileText },
  { to: "/admin/certificates", label: "Certificates", icon: Award },
  { to: "/admin/export", label: "Export Data", icon: FileDown },
];

const AdminLayout = () => {
  const { profile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const visibleNavItems = profile?.hasControls
    ? [...navItems, { to: "/admin/controls", label: "Controls", icon: SlidersHorizontal }]
    : navItems;

  const onLogout = async () => {
    await signOut();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="lg:hidden bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-4 h-16 flex items-center justify-between">
          <BrandLogo size="sm" sub={false} />
          <button onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-slate-100">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="flex">
        <aside className={`${open ? "block" : "hidden"} lg:block w-64 bg-white border-r border-slate-200 lg:h-screen lg:fixed lg:left-0 lg:top-0 z-30 relative`}>
          <div className="hidden lg:flex items-center px-5 h-20 border-b border-slate-200">
            <BrandLogo size="sm" sub={false} />
          </div>

          <nav className="p-4 space-y-1">
            {visibleNavItems.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    active
                      ? "bg-blue-50 text-blue-700 font-semibold"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                A
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">Admin</div>
                <div className="text-xs text-slate-500 truncate">{profile?.email}</div>
              </div>
            </div>
            <button onClick={onLogout} data-testid="admin-logout-btn" className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-50 border border-slate-200">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 lg:ml-64 min-h-screen">
          <div className="p-6 lg:p-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
